import { formatEuros, isoDate, periodPreset, type Dashboard, type DashboardBreakdownRow, type DashboardKpis } from '@clim/domain';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Empty, ErrorBox, Field, Input } from '../components/ui';
import { errorMessage } from '../lib/errors';
import { useReady } from '../lib/session';
import { useTerritory } from '../lib/territory';
import { useOnline } from '../lib/use-online';

type Preset = 'today' | '7d' | 'month' | 'last_month' | 'year';
const PRESETS: [Preset, string][] = [
  ['today', 'Aujourd’hui'],
  ['7d', '7 jours'],
  ['month', 'Mois en cours'],
  ['last_month', 'Mois précédent'],
  ['year', 'Année'],
];
const pct = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)} %`);

/**
 * Tableau de bord (§17) : indicateurs du territoire choisi (ou de tous, toujours indiqué, avec le détail par
 * territoire), analyses de la période et fil d'activité du jour.
 */
export function DashboardPage() {
  const { api } = useReady();
  const online = useOnline();
  const { territoryId } = useTerritory();
  const today = isoDate(new Date());
  const [preset, setPreset] = useState<Preset | null>('month');
  const [range, setRange] = useState(periodPreset('month', today));
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!online) return;
    const q = new URLSearchParams({ from: range.from, to: range.to, ...(territoryId ? { territoryId } : {}) });
    api.request<Dashboard>('GET', `/v1/dashboard?${q}`).then(
      (d) => (setData(d), setError(null)),
      (e) => setError(errorMessage(e)),
    );
  }, [api, online, range, territoryId]);
  const choose = (p: Preset) => {
    setPreset(p);
    setRange(periodPreset(p, today));
  };

  return (
    <div className="dashboard">
      <h1>Tableau de bord</h1>
      <div className="filters">
        <div className="chips" role="tablist" aria-label="Période">
          {PRESETS.map(([p, label]) => (
            <button key={p} role="tab" aria-selected={preset === p} className={`chip${preset === p ? ' on' : ''}`} onClick={() => choose(p)}>
              {label}
            </button>
          ))}
        </div>
        <Field label="Du">
          <Input type="date" value={range.from} onChange={(e) => e.target.value && (setPreset(null), setRange({ ...range, from: e.target.value }))} />
        </Field>
        <Field label="Au">
          <Input type="date" value={range.to} onChange={(e) => e.target.value && (setPreset(null), setRange({ ...range, to: e.target.value }))} />
        </Field>
      </div>
      {!online && <Empty>Le tableau de bord nécessite une connexion.</Empty>}
      <ErrorBox message={error} />
      {data && (
        <>
          <p className={`scope${data.scope.multiple ? ' multi' : ''}`} role="status">
            {data.scope.multiple ? '⚠ ' : ''}
            {data.scope.label} · du {data.from.split('-').reverse().join('/')} au {data.to.split('-').reverse().join('/')}
          </p>
          <Kpis k={data.kpis} />
          {data.byTerritory.length > 0 && <ByTerritory rows={data.byTerritory} />}

          <div className="grid-2">
            <Ranking title="Ventes par commercial" rows={data.salesByCommercial} countLabel="dossiers" money />
            <Ranking title="Produits les plus vendus" rows={data.topProducts} countLabel="unités" money />
            <Ranking title="Marques" rows={data.topBrands} countLabel="unités" money />
            <Ranking title="Puissances" rows={data.topPowers} countLabel="unités" money />
          </div>

          <div className="grid-2">
            <div className="card">
              <h2>Poses par technicien</h2>
              {!data.installsByTechnician.length ? (
                <Empty>Aucune pose sur la période.</Empty>
              ) : (
                <table className="table compact" aria-label="Poses par technicien">
                  <thead>
                    <tr>
                      <th>Technicien</th>
                      <th>Poses</th>
                      <th>SAV</th>
                      <th>Taux de SAV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.installsByTechnician.map((t) => (
                      <tr key={t.key}>
                        <td>{t.label}</td>
                        <td>{t.count}</td>
                        <td>{t.savCount}</td>
                        <td>{pct(t.savRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="card">
              <h2>Commissions et rémunérations</h2>
              {!data.commissions.length ? (
                <Empty>Rien sur la période.</Empty>
              ) : (
                <table className="table compact" aria-label="Commissions et rémunérations">
                  <thead>
                    <tr>
                      <th />
                      <th>À valider</th>
                      <th>Validé</th>
                      <th>Payé</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.commissions.map((c) => (
                      <tr key={c.kind}>
                        <td>{c.label}</td>
                        <td className="nowrap">{formatEuros(c.toValidateCents)}</td>
                        <td className="nowrap">{formatEuros(c.validatedCents)}</td>
                        <td className="nowrap">{formatEuros(c.paidCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="card">
            <h2>Activité du jour</h2>
            {!data.activity.length ? (
              <Empty>Pas encore d’activité aujourd’hui.</Empty>
            ) : (
              <ul className="plain feed" aria-label="Activité du jour">
                {data.activity.map((a, i) => (
                  <li key={i} className={`feed-row tone-line-${a.tone}`}>
                    <span className="muted nowrap">{new Date(a.at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                    <span>{a.dossierId ? <Link to={`/dossiers/${a.dossierId}`}>{a.text}</Link> : a.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Kpis({ k }: { k: DashboardKpis }) {
  const tiles: [string, string, string?, string?][] = [
    ['Dossiers créés', String(k.dossiersCreated)],
    ['Ventes (dossiers validés)', String(k.salesCount), `${formatEuros(k.salesHtCents)} HT`],
    ['CA signé (TTC)', formatEuros(k.signedTtcCents), 'bons de commande signés'],
    ['Installations terminées', String(k.installationsDone)],
    ['Entretiens / dépannages', String(k.maintenancesDone)],
    ['Encaissé', formatEuros(k.paymentsCollectedCents), k.paymentsToRemitCents ? `${formatEuros(k.paymentsToRemitCents)} à remettre` : undefined, k.paymentsToRemitCents ? '/encaissements' : undefined],
    ['À contrôler', String(k.toControl), 'maintenant', '/dossiers?statut=submitted'],
    ['Interventions à accepter', String(k.interventionsToAccept), `${k.interventionsRefused} refusée(s) sur la période`, '/planning'],
    ['SAV', String(k.savOpen), `${k.savOpened} ouvert(s) sur la période`, '/sav'],
    ['Stock faible', String(k.stockLow), 'références', '/stock'],
  ];
  return (
    <dl className="kpis" aria-label="Indicateurs">
      {tiles.map(([label, value, hint, to]) => (
        <div key={label} className="kpi">
          <dt>{label}</dt>
          <dd>{to ? <Link to={to}>{value}</Link> : value}</dd>
          {hint && <span className="muted">{hint}</span>}
        </div>
      ))}
    </dl>
  );
}

function ByTerritory({ rows }: { rows: Dashboard['byTerritory'] }) {
  return (
    <div className="card">
      <h2>Détail par territoire</h2>
      <table className="table compact" aria-label="Détail par territoire">
        <thead>
          <tr>
            <th>Territoire</th>
            <th>Dossiers</th>
            <th>Ventes</th>
            <th>CA HT</th>
            <th>Poses</th>
            <th>Encaissé</th>
            <th>À contrôler</th>
            <th>SAV ouverts</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.territoryId}>
              <td>{t.name}</td>
              <td>{t.dossiersCreated}</td>
              <td>{t.salesCount}</td>
              <td className="nowrap">{formatEuros(t.salesHtCents)}</td>
              <td>{t.installationsDone}</td>
              <td className="nowrap">{formatEuros(t.paymentsCollectedCents)}</td>
              <td>{t.toControl}</td>
              <td>{t.savOpen}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Classement : une barre par ligne (une seule série, une seule teinte), valeur toujours écrite. */
function Ranking({ title, rows, countLabel, money }: { title: string; rows: DashboardBreakdownRow[]; countLabel: string; money?: boolean }) {
  const max = Math.max(1, ...rows.map((r) => (money ? (r.amountCents ?? 0) : r.count)));
  return (
    <div className="card">
      <h2>{title}</h2>
      {!rows.length && <Empty>Aucune vente sur la période.</Empty>}
      {rows.map((r) => {
        const v = money ? (r.amountCents ?? 0) : r.count;
        const text = `${r.label} : ${money ? `${formatEuros(v)} HT · ` : ''}${r.count.toLocaleString('fr-FR')} ${countLabel}`;
        return (
          <div key={r.key} className="rank-row" title={text}>
            <span className="rank-label">{r.label}</span>
            <span className="rank-track">
              <span className="rank-bar" style={{ width: `${(v / max) * 100}%` }} />
            </span>
            <span className="rank-value">
              <strong>{money ? formatEuros(v) : r.count}</strong>
              <span className="muted">
                {' '}
                · {r.count.toLocaleString('fr-FR')} {countLabel}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
